import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { CONSENT_STORAGE_KEY, MAX_TIMEOUT_MS, boundaryActive, canonicalPath, crossesBoundary, disableIfLoaded, emitConsentChange, enforceCurrentConsent, failClosedNavigate, failClosedReload, getStoredExpiry, isGoogleLoaded, maybeLoadGoogle } from '../../lib/analyticsConsent';
import { useAnalyticsConsent } from '../../hooks/useAnalyticsConsent';
import { ConsentCard } from './ConsentCard';
type HistoryMethod = History['pushState'];

/** True when a same-origin move to this URL must leave the current document. */
function mustLeaveDocument(target: URL): boolean {
  if (target.origin !== window.location.origin) return false;
  const from = canonicalPath(window.location.pathname);
  const to = canonicalPath(target.pathname);
  if (from === to) return false; // same-path anchors and state updates stay in place
  return isGoogleLoaded() || crossesBoundary(from, to);
}

/**
 * Keeps Google code confined to /parents and /pricing documents:
 * - history.pushState/replaceState and same-tab links that cross the boundary become full loads
 * - fail-closed reload on back/forward after Google loaded
 * - re-checks consent on visibility, pageshow, other-tab changes and saved-choice expiry
 * Collection is always disabled before any reload or navigation from a loaded document.
 */
export function AnalyticsGate() {
  const {
    pathname
  } = useLocation();
  const snapshot = useAnalyticsConsent();
  const prevPath = useRef(canonicalPath(pathname));

  // Route changes that slipped past the wrappers (e.g. popstate).
  useEffect(() => {
    const path = canonicalPath(pathname);
    const prev = prevPath.current;
    prevPath.current = path;
    if (prev !== path && (isGoogleLoaded() || boundaryActive() && crossesBoundary(prev, path))) {
      failClosedReload();
      return;
    }
    maybeLoadGoogle(path);
  }, [pathname, snapshot.choice, snapshot.signalDenied, snapshot.storageAvailable]);

  // Saved-choice expiry: an open tab must not keep collecting after consent lapses.
  useEffect(() => {
    const expiresAt = getStoredExpiry();
    if (expiresAt === null) return undefined;
    let timer = 0;
    const schedule = () => {
      const remaining = expiresAt - Date.now();
      if (remaining <= 0) {
        if (enforceCurrentConsent()) emitConsentChange();
        return;
      }
      timer = window.setTimeout(schedule, Math.min(remaining + 50, MAX_TIMEOUT_MS));
    };
    schedule();
    return () => window.clearTimeout(timer);
  }, [snapshot.choice]);

  // Global listeners and history wrappers, installed once.
  useEffect(() => {
    const recheck = () => {
      // Enforce first, then let React re-render (and possibly load) from fresh state.
      if (!enforceCurrentConsent()) return;
      emitConsentChange();
      maybeLoadGoogle(window.location.pathname);
    };
    const onClick = (event: MouseEvent) => {
      if (!boundaryActive()) return;
      if (event.defaultPrevented || event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const anchor = (event.target as Element | null)?.closest?.('a[href]') as HTMLAnchorElement | null;
      if (!anchor) return;
      if (anchor.hasAttribute('download')) return;
      const target = anchor.getAttribute('target');
      if (target && target !== '_self') return;
      let url: URL;
      try {
        url = new URL(anchor.href, window.location.href);
      } catch {
        return;
      }
      if (!mustLeaveDocument(url)) return;
      event.preventDefault();
      failClosedNavigate(url.href, 'assign');
    };
    const onPageShow = () => recheck();
    const onVisibility = () => {
      if (document.visibilityState === 'visible') recheck();
    };
    const onStorage = (event: StorageEvent) => {
      if (event.key !== null && event.key !== CONSENT_STORAGE_KEY) return;
      recheck();
    };
    const onPopState = () => disableIfLoaded();
    document.addEventListener('click', onClick, true);
    window.addEventListener('pageshow', onPageShow);
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('storage', onStorage);
    window.addEventListener('popstate', onPopState, true);

    // Synchronous history guards, only where Google could ever load.
    const originalPush = window.history.pushState;
    const originalReplace = window.history.replaceState;
    if (boundaryActive()) {
      const wrap = (original: HistoryMethod, mode: 'assign' | 'replace'): HistoryMethod => function guarded(this: History, data: unknown, unused: string, url?: string | URL | null) {
        if (url !== undefined && url !== null) {
          let target: URL | null = null;
          try {
            target = new URL(String(url), window.location.href);
          } catch {
            target = null;
          }
          if (target && mustLeaveDocument(target)) {
            failClosedNavigate(target.href, mode);
            return;
          }
        }
        return original.call(this, data, unused, url);
      };
      window.history.pushState = wrap(originalPush, 'assign');
      window.history.replaceState = wrap(originalReplace, 'replace');
    }
    return () => {
      document.removeEventListener('click', onClick, true);
      window.removeEventListener('pageshow', onPageShow);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('popstate', onPopState, true);
      window.history.pushState = originalPush;
      window.history.replaceState = originalReplace;
    };
  }, []);
  return <ConsentCard data-id="element-97" data-mp="39sldawmusn22" />;
}